import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, CardContent, Chip, CircularProgress, Divider, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { subDays } from 'date-fns';

import MultiSelectDropdown from './components/multiSelectDropdown';
import TableFilterSearch from './components/tableFilterSearch';
import './index.scss';
import { getInspectionCategories, getInspectionTypes, getInspectionLayouts } from '../redux/slice';
import { TABLE_FILTER_CONFIG, TABLE_FILTER_QUERY_KEYS } from './constants/tableFilterConfig';
import TagTreeFilter from '../../../customControls/TagTreeFilter';
import { useTagsManage } from 'Modules/.common/components/TagsManage/useTagsManage';
import DateRangeFilter from './components/dateRangeFilter';

/**
 * Get all descendant tag IDs for a given tag ID
 * @param {string} tagId - The parent tag ID
 * @param {Array} tags - Flat array of all tags with parentId property
 * @returns {string[]} - Array of all descendant tag IDs (not including the parent)
 */
const getAllDescendantTagIds = (tagId, tags) => {
  const descendants = [];
  const tagIdStr = String(tagId);

  // Find direct children
  const directChildren = tags.filter((tag) => String(tag.parentId) === tagIdStr);

  directChildren.forEach((child) => {
    const childIdStr = String(child.id);
    descendants.push(childIdStr);
    // Recursively get descendants of this child
    descendants.push(...getAllDescendantTagIds(childIdStr, tags));
  });

  return descendants;
};

const buildChipQueryState = (chips = []) => chips.reduce((acc, chip) => {
  const config = TABLE_FILTER_CONFIG[chip.type];
  if (!config || !chip.id) {
    return acc;
  }

  if (!acc[config.queryKey]) {
    acc[config.queryKey] = [];
  }

  acc[config.queryKey].push(chip.id);
  return acc;
}, {});

const mergeChipsIntoQueries = (previousQueries, chips) => {
  if (!previousQueries) {
    return previousQueries;
  }

  const nextQueries = { ...previousQueries };
  TABLE_FILTER_QUERY_KEYS.forEach((key) => {
    delete nextQueries[key];
  });

  return {
    ...nextQueries,
    ...buildChipQueryState(chips),
  };
};

const FilterCard = ({ onQueryStringsChange }) => {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const [filterCardQueryStrings, setFilterCardQueryStrings] = useState({
    dates: [
      { startTime: subDays(new Date(), 7).toISOString(), endTime: new Date().toISOString() },
    ],
  });
  const [tableFilterChips, setTableFilterChips] = useState([]);
  const [tableFilterSearchValue, setTableFilterSearchValue] = useState('');
  const tableFilterSearchRef = useRef(null);
  const { inspectionLayouts, inspectionTypes, inspectionCategories } = useSelector((state) => state.inspectionHistoryRedux);
  const { tags: tagTreeData = [], isLoading: tagsLoading } = useTagsManage({ onlyTags: true });
  const selectedIdsByType = useMemo(() => tableFilterChips.reduce((acc, chip) => {
    if (!acc[chip.type]) {
      acc[chip.type] = new Set();
    }

    acc[chip.type].add(chip.id);
    return acc;
  }, {}), [tableFilterChips]);
  const tagDropdownOptions = useMemo(() => (tagTreeData || [])
    .filter((tag) => tag?.id !== undefined && tag?.label)
    .map((tag) => ({
      label: tag.label,
      value: String(tag.id),
    })
  ), [tagTreeData]);

  useEffect(() => {
    dispatch(getInspectionLayouts());
    dispatch(getInspectionTypes());
    dispatch(getInspectionCategories());
  }, []);

  useEffect(() => {
    onQueryStringsChange(filterCardQueryStrings);
  }, [filterCardQueryStrings]);

  const verificationTypeOptions = [
    {
      label: messages['evirPx_Incomplete'],
      value: 'incomplete',
    },
    {
      label: messages['evirPx_Unverified'],
      value: 'unverified',
    },
  ];

  const severityOptions = [
    {
      label: messages['evirPx_Major_Defects'],
      value: '255',
      icon: '/assets/icons/severity-major.svg',
    },
    {
      label: messages['evirPx_Minor_Defects'],
      value: '63,127',
      icon: '/assets/icons/severity-minor.svg',
    },
    {
      label: messages['evirPx_No_Defects'],
      value: '0',
      icon: '/assets/icons/severity-none.svg',
    },
  ];

  const handleVerificationTypesChange = (value) => {
    setFilterCardQueryStrings((prev) => {
      if (!prev) {
        return prev;
      }

      const { verification, ...rest } = prev;
      const verificationTypesField = value.length > 0
        ? { verification: value }
        : {};
      return {
        ...rest,
        ...verificationTypesField,
      };
    });
  };

  const handleTableFilterSelection = useCallback((type, record) => {
    const config = TABLE_FILTER_CONFIG[type];
    if (!config) {
      return;
    }

    const id = config.getId(record);
    if (!id) {
      return;
    }

    setTableFilterChips((prev) => {
      if (prev.some((chip) => chip.type === type && chip.id === id)) {
        return prev;
      }

      const chip = {
        type,
        id,
        label: config.getPrimaryLabel(record),
        groupLabel: messages[config.groupLabelKey] || config.fallbackGroupLabel,
      };
      const nextChips = [...prev, chip];
      setFilterCardQueryStrings((prevQueries) => mergeChipsIntoQueries(prevQueries, nextChips));
      return nextChips;
    });
  }, [messages]);

  const handleTableFilterChipRemove = useCallback((chipToRemove) => {
    setTableFilterChips((prev) => {
      const nextChips = prev.filter((chip) => !(chip.type === chipToRemove.type && chip.id === chipToRemove.id));
      setFilterCardQueryStrings((prevQueries) => mergeChipsIntoQueries(prevQueries, nextChips));
      return nextChips;
    });
  }, []);

  const handleSeveritiesChange = (value) => {
    setFilterCardQueryStrings((prev) => {
      if (!prev) {
        return prev;
      }

      const { severities, ...rest } = prev;
      const severitiesField = value.length > 0
        ? { severities: value }
        : {};
      return {
        ...rest,
        ...severitiesField,
      };
    });
  };

  const handleInspectionNamesChange = (value) => {
    setFilterCardQueryStrings((prev) => {
      if (!prev) {
        return prev;
      }

      const { inspectionNameIds, ...rest } = prev;
      const inspectionNamesField = value.length > 0
        ? { inspectionNameIds: value }
        : {};
      return {
        ...rest,
        ...inspectionNamesField,
      };
    });
  };

  const handleInspectionTypesChange = (value) => {
    setFilterCardQueryStrings((prev) => {
      if (!prev) {
        return prev;
      }

      const { inspectionTypeIds, ...rest } = prev;
      const inspectionTypesField = value.length > 0
        ? { inspectionTypeIds: value }
        : {};
      return {
        ...rest,
        ...inspectionTypesField,
      };
    });
  };

  const handeAssetTypesChange = (value) => {
    setFilterCardQueryStrings((prev) => {
      if (!prev) {
        return prev;
      }
      const { zoneLayoutIds, ...rest } = prev;
      const zoneLayoutField = value.length > 0
        ? { zoneLayoutIds: value }
        : {};
      return {
        ...rest,
        ...zoneLayoutField,
      };
    });
  };

  const handleTagsChange = (value) => {
    setFilterCardQueryStrings((prev) => {
      if (!prev) {
        return prev;
      }

      const { tagIds, ...rest } = prev;
      const tagField = value.length > 0
        ? { tagIds: value }
        : {};
      return {
        ...rest,
        ...tagField,
      };
    });
  };

  const currentDateRangeValue = useMemo(() => {
    const range = filterCardQueryStrings?.dates?.[0];

    if (!range) {
      return null;
    }

    return {
      startDate: new Date(range.startTime),
      endDate: new Date(range.endTime),
    };
  }, [
    filterCardQueryStrings?.dates?.[0]?.startTime,
    filterCardQueryStrings?.dates?.[0]?.endTime,
  ]);

  const renderTagTreeOptions = useCallback(({ searchValue, pendingValues = [], setPendingValues }) => {
    if (tagsLoading) {
      return (
        <div className='tag-tree-dropdown-loading'>
          <CircularProgress size={20} />
        </div>
      );
    }

    if (!tagTreeData?.length) {
      return (
        <Typography variant='body2' color='text.secondary'>
          {messages['evirPx_No_Results_Found'] || 'No tags available'}
        </Typography>
      );
    }

    const selectedIds = new Set((pendingValues || []).map((id) => String(id)));

    // Handle tag selection - select the tag and all its descendants
    const handleTagSelect = (tag) => {
      const tagIdStr = String(tag.id);
      const descendantIds = getAllDescendantTagIds(tagIdStr, tagTreeData);
      const allIdsToAdd = [tagIdStr, ...descendantIds];

      setPendingValues((prev) => {
        const newSet = new Set(prev);
        allIdsToAdd.forEach((id) => newSet.add(id));
        return Array.from(newSet);
      });
    };

    // Handle tag deselection - deselect the tag and all its descendants
    const handleTagDeselect = (tag) => {
      const tagIdStr = String(tag.id);
      const descendantIds = getAllDescendantTagIds(tagIdStr, tagTreeData);
      const allIdsToRemove = new Set([tagIdStr, ...descendantIds]);

      setPendingValues((prev) => prev.filter((id) => !allIdsToRemove.has(id)));
    };

    return (
      <TagTreeFilter
        tags={tagTreeData}
        searchText={searchValue}
        noResultsLabel={messages['evirPx_No_Results_Found']}
        selectedTagIds={selectedIds}
        onTagSelect={handleTagSelect}
        onTagDeselect={handleTagDeselect}
        height={null}
      />
    );
  }, [messages, tagTreeData, tagsLoading]);

  const handleDateRangeChange = ({ startDate, endDate }) => {
    setFilterCardQueryStrings((prev) => {
      if (!prev) {
        return prev;
      }

      if (!startDate || !endDate) {
        return prev;
      }

      return {
        ...prev,
        dates: [
          {
            startTime: startDate.toISOString(),
            endTime: endDate.toISOString(),
          },
        ],
      };
    });
  };

  const hasActiveFilters = useMemo(() => {
    const { dates, ...otherFilters } = filterCardQueryStrings || {};
    const hasDropdownFilters = Object.keys(otherFilters).length > 0;
    const hasChipFilters = tableFilterChips.length > 0;
    const hasSearchValue = tableFilterSearchValue.trim().length > 0;
    return hasDropdownFilters || hasChipFilters || hasSearchValue;
  }, [filterCardQueryStrings, tableFilterChips, tableFilterSearchValue]);

  const handleClearFilters = useCallback(() => {
    // Reset to default state with only dates
    setFilterCardQueryStrings({
      dates: [
        { startTime: subDays(new Date(), 7).toISOString(), endTime: new Date().toISOString() },
      ],
    });
    // Clear all search chips
    setTableFilterChips([]);
    // Clear the search input
    tableFilterSearchRef.current?.clearSearch();
  }, []);

  return (
    <Card variant='outlined' className='filter-card' data-testid='filter-card'>
      <CardContent className='filter-card-content'>
        <Grid container spacing={2} className='filter-card-content-items'>
          <Grid item xs={5} className='left-item'>
            <TableFilterSearch
              onResultSelect={handleTableFilterSelection}
              selectedIdsByType={selectedIdsByType}
              onSearchValueChange={setTableFilterSearchValue}
              searchInputRef={tableFilterSearchRef}
            />
            <Divider orientation='vertical' className='filter-card-divider' />
          </Grid>
          <Grid item xs={7} className='right-item'>
            <DateRangeFilter
              label={messages['evirPx_Date_Range']}
              value={currentDateRangeValue}
              onApply={handleDateRangeChange}
            />
            <MultiSelectDropdown
              dropdownLabel={messages['evirPx_Asset_Type']}
              options={inspectionLayouts}
              value={filterCardQueryStrings.zoneLayoutIds}
              multipleLabel={messages['evirPx_Multiple']}
              searchPlaceholder={messages['evirPx_Search']}
              clearLabel={messages['evirPx_Clear_Selected']}
              cancelLabel={messages['evirPx_Cancel']}
              applyLabel={messages['evirPx_Apply']}
              noResultsLabel={messages['evirPx_No_Results_Found']}
              onApply={(value) => handeAssetTypesChange(value)}
              onCancel={() => { }}
              textField='name'
              valueField='id'
            />
            <MultiSelectDropdown
              dropdownLabel={messages['evirPx_Inspection_Name']}
              options={inspectionTypes}
              value={filterCardQueryStrings.inspectionNameIds}
              multipleLabel={messages['evirPx_Multiple']}
              searchPlaceholder={messages['evirPx_Search']}
              clearLabel={messages['evirPx_Clear_Selected']}
              cancelLabel={messages['evirPx_Cancel']}
              applyLabel={messages['evirPx_Apply']}
              noResultsLabel={messages['evirPx_No_Results_Found']}
              onApply={(value) => handleInspectionNamesChange(value)}
              onCancel={() => { }}
              textField='name'
              valueField='id'
            />
            <MultiSelectDropdown
              dropdownLabel={messages['evirPx_Inspection_Type']}
              options={inspectionCategories}
              value={filterCardQueryStrings.inspectionTypeIds}
              multipleLabel={messages['evirPx_Multiple']}
              searchPlaceholder={messages['evirPx_Search']}
              clearLabel={messages['evirPx_Clear_Selected']}
              cancelLabel={messages['evirPx_Cancel']}
              applyLabel={messages['evirPx_Apply']}
              noResultsLabel={messages['evirPx_No_Results_Found']}
              onApply={(value) => handleInspectionTypesChange(value)}
              onCancel={() => { }}
              textField='name'
              valueField='id'
            />
            <MultiSelectDropdown
              dropdownLabel={messages['evirPx_Severity']}
              options={severityOptions}
              value={filterCardQueryStrings.severities}
              multipleLabel={messages['evirPx_Multiple']}
              searchPlaceholder={messages['evirPx_Search']}
              clearLabel={messages['evirPx_Clear_Selected']}
              cancelLabel={messages['evirPx_Cancel']}
              applyLabel={messages['evirPx_Apply']}
              noResultsLabel={messages['evirPx_No_Results_Found']}
              onApply={(value) => handleSeveritiesChange(value)}
              onCancel={() => { }}
            />
            <MultiSelectDropdown
              dropdownLabel={messages['evirPx_Tag']}
              options={tagDropdownOptions}
              value={filterCardQueryStrings.tagIds}
              multipleLabel={messages['evirPx_Multiple']}
              searchPlaceholder={messages['evirPx_Search']}
              clearLabel={messages['evirPx_Clear_Selected']}
              cancelLabel={messages['evirPx_Cancel']}
              applyLabel={messages['evirPx_Apply']}
              noResultsLabel={messages['evirPx_No_Results_Found']}
              onApply={(value) => handleTagsChange(value)}
              onCancel={() => { }}
              renderCustomOptions={renderTagTreeOptions}
              isSearching={tagsLoading}
              textField='label'
              valueField='value'
            />
            <MultiSelectDropdown
              dropdownLabel={messages['evirPx_Verification_Type']}
              options={verificationTypeOptions}
              value={filterCardQueryStrings.verification}
              multipleLabel={messages['evirPx_Multiple']}
              searchPlaceholder={messages['evirPx_Search']}
              clearLabel={messages['evirPx_Clear_Selected']}
              cancelLabel={messages['evirPx_Cancel']}
              applyLabel={messages['evirPx_Apply']}
              noResultsLabel={messages['evirPx_No_Results_Found']}
              onApply={(value) => handleVerificationTypesChange(value)}
              onCancel={() => { }}
            />
            {hasActiveFilters && (
              <Button
                variant="text"
                size="small"
                onClick={handleClearFilters}
                className="clear-filters-button"
              >
                {messages['evirPx_Clear_Filters'] || 'Clear Filters'}
              </Button>
            )}
          </Grid>
        </Grid>
        {tableFilterChips.length > 0 && (
          <div className='table-filter-chip-list'>
            {tableFilterChips.map((chip) => (
              <Chip
                key={`${chip.type}-${chip.id}`}
                label={`${chip.label} (${chip.groupLabel})`}
                className='table-filter-chip'
                size='small'
                onDelete={() => handleTableFilterChipRemove(chip)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FilterCard;

FilterCard.propTypes = {
  onQueryStringsChange: PropTypes.func,
};
